import type { Door, Lock, SyncJob } from './types';

/** Per-door view of "wacht op sync" (mirrors the planned `door_sync_status` view). */
export interface DoorSyncStatus {
  door: Door;
  lock: Lock | null;
  pendingCount: number;
  oldestPendingAt: string | null;
  oldestRevokeAt: string | null;
  failedCount: number;
}

// 'reported' telt als open: een app-melding is pas na reconciliatie bevestigd (security review C1).
const OPEN: SyncJob['status'][] = ['queued', 'awaiting_ble', 'in_progress', 'reported'];
const REVOKE_ACTIONS: SyncJob['action'][] = ['remove_member', 'remove_credential', 'revoke_code'];

export function isOpenJob(j: SyncJob): boolean {
  return OPEN.includes(j.status);
}

export function doorSyncStatuses(doors: Door[], locks: Lock[], jobs: SyncJob[]): DoorSyncStatus[] {
  const lockById = new Map(locks.map((l) => [l.id, l]));
  return doors.map((door) => {
    const lock = door.lock_id ? lockById.get(door.lock_id) ?? null : null;
    const mine = lock ? jobs.filter((j) => j.lock_id === lock.id) : [];
    const open = mine.filter(isOpenJob).sort((a, b) => a.created_at.localeCompare(b.created_at));
    // Mislukte intrekkingen blijven ook meetellen voor de melding (mirror van door_sync_status.revoke_alert).
    const revokes = mine
      .filter((j) => REVOKE_ACTIONS.includes(j.action) && (isOpenJob(j) || j.status === 'failed'))
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    return {
      door,
      lock,
      pendingCount: open.length,
      oldestPendingAt: open[0]?.created_at ?? null,
      oldestRevokeAt: revokes[0]?.created_at ?? null,
      failedCount: mine.filter((j) => j.status === 'failed').length,
    };
  });
}

/** Dashboard tile: doors with open sync, oldest first. */
export function doorsAwaitingSync(statuses: DoorSyncStatus[]): DoorSyncStatus[] {
  return statuses
    .filter((s) => s.pendingCount > 0)
    .sort((a, b) => (a.oldestPendingAt ?? '').localeCompare(b.oldestPendingAt ?? ''));
}

export function isRevokeOverdue(s: DoorSyncStatus, alertHours: number, now = new Date()): boolean {
  if (!s.oldestRevokeAt) return false;
  return now.getTime() - new Date(s.oldestRevokeAt).getTime() > alertHours * 3600_000;
}
